import fetchFromAPI from "../../tools/fetchFromAPI";
import { Container, Typography } from '@material-ui/core';
import { useContext, useEffect, useState } from 'react';
import { useRouter } from "next/router";
import { ScrollContext } from '../../components/providers/scroll';
import { getNewParams, updateRouterData } from '../../functions/plpOps';
import MetaTitle from '../../components/meta/title';
import ProductInfiniteScroll from '../../components/navigation/infinite-scroll';
import { makeStyles } from '@material-ui/styles';
import theme from '../../components/theme';
import Link from "next/link";
import {TrackingContext} from "components/providers/tracking";

const useStyles = makeStyles({
    blogGrid: {
        display: 'grid',
        paddingTop: '20px',
        justifyContent: 'center',
        gridTemplateColumns: "repeat(2, minmax(0, 50%))",
        gap: 10,
        [theme.breakpoints.up('sm')]: {
            gridTemplateColumns: "repeat(3, minmax(0, 33.333%))",
            gap: 20
        },
        [theme.breakpoints.up('md')]: {
            gridTemplateColumns: "repeat(4, minmax(0, 25%))",
            gap: 20
        },
    },
    headerWrapper:{
        background: '#fff',
        backgroundImage: 'url("/images/np-seoblogg-topp-1-1706858854.jpg")',
        backgroundSize: 'cover',
        width: '100%',
        height: '39vh',
        maxHeight: '480px',
        maxWidth: 'unset',
        display: 'flex',
        flexDirection: 'column',
        justifyItems: 'center',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        marginBottom: '2em'
    },
    titleLg: {
        fontFamily: 'didot',
        textTransform: 'uppercase',
        textAlign: 'center',
        color: '#222',
        position: 'relative',
        zIndex: 5,
        fontSize: '48px',
        margin: '1em auto',
    },
    titleSm: {
        fontFamily: 'futura',
        textAlign: 'center',
        color: '#222',
        position: 'relative',
        zIndex: 5,
        fontSize: '16px',
        lineHeight: '1.5',
        whiteSpace: 'break-spaces',
        maxWidth: '600px',
        margin: '1em auto -1.5em',
    },
    topLine: {
        margin: '1em auto -2em',
        textAlign: 'center',
        fontSize: '0.9em',
        display: 'block',
        color: '#575757',
    },
    article: {
        '& a': {
            textDecoration: 'none',
            color: "#222",
        },
        '& img': {
            outline: '1px solid #eee',
            maxWidth: "100%",
        },
        '& h1': {
            fontFamily: 'Chronicle',
            fontWeight: 400,
            margin: '1em 0 -0.5em',
        },
        '& p': {
            fontSize: '0.92em',
            color: '#555'
        }
    },
    date: {
        textAlign: 'center',
        margin: '0 auto 0.5em',
        display: 'block',
        color: '#575757',
        fontStyle: 'italic',
    },
    timeAuthor: {
        display: 'inline',
        fontStyle: 'italic',
        fontSize: '0.9em',
        '& time': {
            display: 'inline',
        },
        '& address': {
            display: 'inline',
            marginLeft: '5px'
        }
    },
    cover: {
        transition: 'opacity 0.2 ease',
        '&:hover': {
            opacity: 0.9,
        }
    },
}, {name: "blogs"})

export default function BlogLandingPage({ title, content }) {
    const router = useRouter();
    const { asPath, query } = router;
    const { page, limit } = query;
    const [initial, setInitial] = useState(null);
    const [posts, setPosts] = useState(content?.entries ? [...content.entries] : []);
    const { setCache, getCache, isPopState } = useContext(ScrollContext);
    const {actions: trackingActions} = useContext(TrackingContext);
    const [pagination, setPagination] = useState({
        currentPage: parseInt(page) || 1,
        previousPage: parseInt(page) || 1,
        initialPage: parseInt(page) || 1,
        loadedPages: [parseInt(page) || 1],
        productLimit: parseInt(limit) || 16,
    });
    const classes = useStyles();

    useEffect(() => {
        trackingActions.pageImpression(content?.intro?.title, 'blog_page');
    }, [])

    useEffect(() => {
        if (!initial) {
            // Update pagination with the now loaded page
            let loadedPages = pagination.loadedPages;
            if (loadedPages.indexOf(content.currentPage) < 0) {
                if (content.currentPage > pagination.initialPage) {
                    loadedPages.push(content.currentPage);
                } else if (content.currentPage) {
                    loadedPages.unshift(content.currentPage);
                }

                setPagination({ ...pagination, loadedPages });

                if (content.currentPage > pagination.previousPage) {
                    setPosts([...posts, ...content.entries])
                } else {
                    setPosts([...content.entries, ...posts])
                }
            }

        } else {
            if(isPopState) {
                setPagination({ initialPage: parseInt(query.page) || 1, loadedPages: [parseInt(query.page) || 1] });
            }
            setPosts([...posts, ...content.entries])
        }
    }, [content.entries]);

    useEffect(() => {
        setPagination(pagination => {
            return { ...pagination, currentPage: parseInt(page) || 1 };
        });
    }, [page]);

    function onPageChange(value) {
        setInitial(false);
        updateUrlParams({ ...getNewParams(value, query) });
    }

    function updateUrlParams({ page, order, slug, ...filters }) {
        const { routerData, replace } = updateRouterData({
            page,
            order,
            initialPage: pagination.initialPage,
            pathname: router.asPath.split('?')[0],
        });

        if (replace) {
            router.replace(routerData, undefined, { scroll: false }).catch(e => console.log(e));
        } else {
            router.push(routerData, undefined, { scroll: false }).catch(e => console.log(e));
        }
    }

    return (
        <>
            <MetaTitle title={content?.intro?.title || "Newport blogs"} />

            <Container className={classes.headerWrapper} disableGutters >
                <div className={classes.headerImg}></div>
            </Container>

            <Container>
                { content?.intro ?
                    <>

                        <span className={classes.topLine} >{content?.intro?.top_line}</span>
                        <Typography className={classes.titleLg} variant={"h1"}>{content?.intro?.title}</Typography>
                        <div className={classes.titleSm} dangerouslySetInnerHTML={{__html: content?.intro?.content}}></div>
                    </>
                    :
                    <>
                        <Typography className={classes.titleLg} variant={"h1"}>The Newport Blog</Typography>
                    </>
                }

                {!initial && content.totalEntries > pagination.productLimit ? <ProductInfiniteScroll
                    currentPage={pagination.currentPage}
                    loadedPages={pagination.loadedPages}
                    loadedProducts={posts.length}
                    totalProducts={content.totalEntries}
                    productsPerPage={pagination.productLimit}
                    changePage={onPageChange}
                    position={'top'}
                /> : ''}

                <section className={classes.blogGrid}>
                    { posts?.map((entry, key)=> {
                        return (
                            <article key={'article-' + key} className={classes.article}>
                                <Link href={`${router.pathname}/${entry.slug}`}>
                                    <a className={classes.cover} >
                                        <img src={entry?.media?.url || '/images/placeholder/product.jpg'} alt={''} />
                                        <h1>{entry?.title}</h1>
                                        <p>{entry?.ingress}</p>
                                        <span class={classes.timeAuthor}>
                                            <time>
                                                {new Date(entry?.date).toLocaleDateString('sv-SE', {
                                                    month: 'long',
                                                    day: 'numeric',
                                                    year: 'numeric',
                                                })}
                                            </time>
                                            <address>Niclas Olsson</address>
                                        </span>
                                    </a>
                                </Link>
                            </article>
                        );
                    })
                    }
                </section>

                <ProductInfiniteScroll
                    currentPage={pagination.currentPage}
                    loadedPages={pagination.loadedPages}
                    loadedProducts={posts.length}
                    totalProducts={content.totalEntries}
                    productsPerPage={pagination.productLimit}
                    changePage={onPageChange}
                    position={'bottom'}
                    productNameLabel={" "}
                />

            </Container>
        </>
    );
}

export const getServerSideProps = async ({ req, res, resolvedUrl }) => {
    let response = await fetchFromAPI({ req, res, asPath: resolvedUrl, pathname: resolvedUrl });

    if (response.status === 'success') {
        return { props: { ...response.data } };
    } else {
        return {
            notFound: true
        };
    }
};